import {
  useTeamOnboardingChecklistDismissCheckItemMutation,
  useTeamOnboardingChecklistQuery,
} from '@trello/graphql/src/generated';
import { usesEnglish } from '@trello/locale';
import { forNamespace } from 'app/common/lib/util/i18n';

const format = forNamespace(['team onboarding', 'checklist']);

export enum TeamOnboardingChecklistKey {
  CreateTeam = 'create-team',
  AddABoard = 'add-a-board',
  AddAPowerUp = 'add-a-power-up',
  ManageTeamSettings = 'manage-team-settings',
  SetMemberPermissions = 'set-member-permissions',
}

export const TEAM_ONBOARDING_OTMD_KEY = (
  orgId: string,
  key: TeamOnboardingChecklistKey,
) => `team-onboarding-v1-${orgId}-${key}`;

interface TeamOnboardingChecklistItem {
  key: TeamOnboardingChecklistKey;
  title: string;
  subtitle?: string;
  href?: string;
  completed: boolean;
}

interface GenerateChecklistItemsArgs {
  orgId: string;
  orgName: string;
  oneTimeMessagesDismissed: string[];
  isFreeTrial: boolean;
  isEnglish: boolean;
  latestBoardPath?: string;
}

const generateChecklistItems = ({
  orgId,
  orgName,
  oneTimeMessagesDismissed,
  isFreeTrial,
  isEnglish,
  latestBoardPath,
}: GenerateChecklistItemsArgs): TeamOnboardingChecklistItem[] => {
  const isComplete = (key: TeamOnboardingChecklistKey) =>
    oneTimeMessagesDismissed.includes(TEAM_ONBOARDING_OTMD_KEY(orgId, key));

  return [
    {
      key: TeamOnboardingChecklistKey.CreateTeam,
      title: format([
        'item-1',
        isFreeTrial ? 'title-free-trial' : 'title-paid',
      ]),
      completed: true,
    },
    {
      key: TeamOnboardingChecklistKey.AddABoard,
      title: format(['item-2', 'title']),
      subtitle: format(['item-2', 'subtitle']),
      href: isEnglish ? '/templates' : `/${orgName}`,
      completed: isComplete(TeamOnboardingChecklistKey.AddABoard),
    },
    {
      key: TeamOnboardingChecklistKey.AddAPowerUp,
      title: format(['item-3', 'title']),
      subtitle: format(['item-3', 'subtitle']),
      href: latestBoardPath ?? '/power-ups',
      completed: isComplete(TeamOnboardingChecklistKey.AddAPowerUp),
    },
    {
      key: TeamOnboardingChecklistKey.ManageTeamSettings,
      title: format(['item-4', 'title']),
      subtitle: format(['item-4', 'subtitle']),
      href: `/${orgName}/account`,
      completed: isComplete(TeamOnboardingChecklistKey.ManageTeamSettings),
    },
    {
      key: TeamOnboardingChecklistKey.SetMemberPermissions,
      title: format(['item-5', 'title']),
      subtitle: format(['item-5', 'subtitle']),
      href: `/${orgName}/members`,
      completed: isComplete(TeamOnboardingChecklistKey.SetMemberPermissions),
    },
  ];
};

interface UseTeamOnboardingChecklistReturn {
  loading: boolean;
  checklistItems: TeamOnboardingChecklistItem[];
  completedCount: number;
  dismissChecklistItem: (key: TeamOnboardingChecklistKey) => void;
}
export const useTeamOnboardingChecklist = (
  orgId: string,
): UseTeamOnboardingChecklistReturn => {
  const { data, loading } = useTeamOnboardingChecklistQuery({
    variables: { orgId },
  });
  const [
    dismissChecklistItemMutation,
  ] = useTeamOnboardingChecklistDismissCheckItemMutation();

  // Return early on load to prevent un-necessary format renders
  if (loading) {
    return {
      loading,
      checklistItems: [],
      completedCount: 0,
      dismissChecklistItem: () => {},
    };
  }

  // Get the on time messages
  const oneTimeMessagesDismissed = (data?.member?.oneTimeMessagesDismissed ??
    []) as string[];

  // Team has a free trial credit
  const isFreeTrial = data?.organization?.credits
    ? data?.organization?.credits.length > 0
    : false;

  // User is English locale
  const isEnglish = usesEnglish();

  // Sort team boards and get latest url
  const boards = data?.organization?.boards ?? [];
  const latestBoard =
    boards.length > 0
      ? boards.reduce((newestBoard, board) =>
          board?.dateLastActivity! > newestBoard?.dateLastActivity!
            ? board
            : newestBoard,
        )
      : undefined;

  let latestBoardPath: string | undefined;
  if (latestBoard) {
    const latestBoardURL = new URL(latestBoard.url);
    latestBoardURL.searchParams.set('cameFromGettingStarted', 'true');
    latestBoardPath = latestBoardURL.pathname + latestBoardURL.search;
  }

  // Generate the list of checklist items using the conditionals above
  const checklistItems = generateChecklistItems({
    orgId,
    orgName: data?.organization?.name ?? '',
    oneTimeMessagesDismissed,
    isFreeTrial,
    isEnglish,
    latestBoardPath,
  });
  const completedCount = checklistItems.filter(({ completed }) => completed)
    .length;
  const dismissChecklistItem = (key: TeamOnboardingChecklistKey) =>
    dismissChecklistItemMutation({
      variables: { messageId: TEAM_ONBOARDING_OTMD_KEY(orgId, key) },
      optimisticResponse: {
        __typename: 'Mutation',
        addOneTimeMessagesDismissed: {
          id: 'me',
          oneTimeMessagesDismissed: oneTimeMessagesDismissed!.concat([
            TEAM_ONBOARDING_OTMD_KEY(orgId, key),
          ]),
          __typename: 'Member',
        },
      },
    });

  return { loading, checklistItems, completedCount, dismissChecklistItem };
};
